import { BreakpointObserver } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, Component, effect, inject, PLATFORM_ID, signal, ViewChild } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogModule } from '@angular/material/dialog';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatTooltipModule } from '@angular/material/tooltip';
import { filter } from 'rxjs';

// Import Services
import { AuthService } from './features/auth/auth.service';
import { ProfileService } from './features/profile/profile.service';
import { DialogService } from './core/dialog';

// Import Shared Components
import { AvatarComponent } from './shared/avatar/avatar';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NistImgComponent } from './shared/nist-img/nist-img.component';
import { MatButtonModule } from '@angular/material/button';
import { MatRippleModule } from '@angular/material/core';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    MatToolbarModule,
    MatButtonModule,
    MatRippleModule,
    MatIconModule,
    MatDialogModule,
    MatSidenavModule,
    MatTooltipModule,
    AvatarComponent,
    NistImgComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class AppComponent {
  private static readonly MOBILE_BREAKPOINT = '(max-width: 960px)';

  @ViewChild(MatSidenav) sidenav?: MatSidenav;
  currentYear = new Date().getFullYear();
  twitterUrl = 'https://x.com/noodl_ist';
  instagramUrl = 'https://instagram.com/noodl.ist';
  youtubeUrl = 'https://www.youtube.com/@noodlist';
  isMobile = false;
  drawerOpen = true;


  // --- Reactive State (now Signals) ---
  // --- Injected Services ---
  private authService = inject(AuthService);
  // We get the signals directly from the services
  session = this.authService.session;
  isAdmin = this.authService.isAdmin;
  private profileService = inject(ProfileService);
  profile = this.profileService.profile;
  private dialogService = inject(DialogService);
  private router = inject(Router);
  isHomePage = signal(this.router.url === '/' || this.router.url === '');
  private platformId = inject(PLATFORM_ID);
  private breakpointObserver = inject(BreakpointObserver);

  constructor() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.isHomePage.set(event.urlAfterRedirects === '/' || event.urlAfterRedirects === '');
        this.closeIfMobile();
      });

    this.setupResponsiveSidenav();

    effect(() => {
      if (!isPlatformBrowser(this.platformId)) {
        return;
      }

      if (this.isHomePage()) {
        document.body.classList.add('home-mode');
      } else {
        document.body.classList.remove('home-mode');
      }
    });
  }

  // --- Methods ---
  signOut() {
    this.authService.signOut();
  }

  openLogin(): void {
    this.dialogService.openLoginDialog();
  }

  openRegister(): void {
    this.dialogService.openRegisterDialog();
  }

  openAbout(): void {
    this.dialogService.openAboutDialog();
  }

  openSuggest(): void {
    this.dialogService.openSuggestDialog();
  }

  openContact(): void {
    this.dialogService.openContactDialog();
  }

  handlePlaceholderClick(event: Event, url?: string | null): void {
    if (!url) {
      event.preventDefault();
    }
  }

  toggleSidenav(): void {
    this.sidenav?.toggle();
  }

  closeIfMobile(): void {
    if (this.isMobile) {
      this.sidenav?.close();
    }
  }

  private setupResponsiveSidenav(): void {
    if (!isPlatformBrowser(this.platformId)) {
      // Favor the desktop layout during prerender so wide screens do not
      // shift when hydration opens the side nav.
      this.isMobile = false;
      this.drawerOpen = true;
      return;
    }

    this.isMobile = this.breakpointObserver.isMatched(AppComponent.MOBILE_BREAKPOINT);
    this.drawerOpen = !this.isMobile;

    this.breakpointObserver
      .observe([AppComponent.MOBILE_BREAKPOINT])
      .pipe(takeUntilDestroyed())
      .subscribe((state) => {
        this.isMobile = state.matches;
        this.drawerOpen = !this.isMobile;
        if (!this.isMobile) {
          this.sidenav?.open();
        } else {
          this.sidenav?.close();
        }
      });
  }
}
